/* Store */

import Alpine from "alpinejs";

declare module "alpinejs" {
  interface Alpine {
    store(name: "cart"): typeof CART_STORE;
  }
}

const _CART_STORE_IMPLEMENTATION = {
  fromPayload(payload: Cart) {
    // Quirk Transforms
    if (payload.cart_level_discount_applications?.[0]?.discount_application) {
      payload.cart_level_discount_applications.forEach((discount) => {
        Object.assign(discount, discount.discount_application);
      });
    }

    Object.assign(this, payload);
  },
  async fetch() {
    await fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((cart) => this.fromPayload(cart));
  },
};

const CART_STORE = _CART_STORE_IMPLEMENTATION as Cart & typeof _CART_STORE_IMPLEMENTATION;

export function initCartStore() {
  Alpine.store("cart", CART_STORE);
}

/* Methods */

type ItemAddPayload = {
  id: string;
  quantity: number;
  selling_plan?: string;
  properties?: Record<string, any>;
};

export async function addToCart({ items }: { items: ItemAddPayload[] }) {
  await fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });

  const payload = await window.Alpine.store("cart").fetch();

  window.dispatchEvent(new CustomEvent("cranky:cart:update", { detail: payload }));
}

export async function updateCartItemQuantity({ 
  id, 
  quantity, 
  item, 
  retryCount = 0 
}: { 
  id: string; 
  quantity: number; 
  item?: CartItem;
  retryCount?: number;
}) {
  try {
    const response = await fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, quantity }),
    });

    const payload = await response.json();

    // Check if there was an error in the response
    if (!response.ok || payload.description?.includes('no valid id or line parameter')) {
      throw new Error(payload.description || payload.message || 'Invalid cart item ID');
    }

    const cartPayload = await window.Alpine.store("cart").fetch();
    window.dispatchEvent(new CustomEvent("cranky:cart:update", { detail: cartPayload }));

  } catch (error: any) {
    if (error.message.includes('no valid id or line parameter') && retryCount < 2 && item) {
      await window.Alpine.store("cart").fetch();
      
      const currentCart = window.Alpine.store("cart");
      const updatedItem = currentCart.items.find((cartItem: CartItem) => 
        cartItem.variant_id === item.variant_id &&
        JSON.stringify(cartItem.properties || {}) === JSON.stringify(item.properties || {}) &&
        (cartItem.selling_plan_allocation?.selling_plan?.id || null) === (item.selling_plan_allocation?.selling_plan?.id || null)
      );
      
      if (updatedItem) {
        return updateCartItemQuantity({ 
          id: updatedItem.key, 
          quantity, 
          item: updatedItem, 
          retryCount: retryCount + 1 
        });
      }
    }
    throw error;
  }
}

export async function addBundleToCart({
  type,
  bundleId,
  bundleSku,
  bundleTitle,
  bundledItems,
  selling_plan,
}: {
  type: "real" | "virtual";
  bundleId: string;
  bundleSku: string;
  bundleTitle: string;
  bundledItems: (Omit<ItemAddPayload, "selling_plan"> & { title: string; compare_at_price: number })[];
  selling_plan?: string;
}) {
  const groupId = crypto.randomUUID();
  const items =
    type === "real"
      ? [
          {
            id: bundleId,
            quantity: 1,
            selling_plan,
            properties: { _components: JSON.stringify(bundledItems) },
          },
        ]
      : bundledItems.map((item) => ({
          ...item,
          selling_plan,
          properties: {
            _group_id: groupId,
            _compare_at_price: item.compare_at_price,
            _bundle_type: type,
            _bundle_id: bundleId,
            _bundle_name: bundleTitle,
            _bundle_sku: bundleSku,
            Bundle: bundleTitle,
          },
        }));

  await addToCart({ items });
}

/* Types */

interface Cart {
  token: string;
  note: string;
  attributes: Record<string, any>;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: CartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: DiscountApplication[];
  items_added: ItemChange[];
  items_removed: ItemChange[];
}

interface CartItem {
  id: number;
  properties: Record<string, any>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  product_title: string;
  variant_title: string | null;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  presentment_price: number;
  total_discount: number;
  discounts: Discount[];
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_has_only_default_variant: boolean;
  gift_card: boolean;
  final_price: number;
  final_line_price: number;
  url: string;
  featured_image: Image;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  untranslated_product_title: string;
  product_description: string;
  untranslated_variant_title: string | null;
  variant_options: string[];
  options_with_values: OptionWithValue[];
  line_level_discount_allocations: any[]; // Adjust this type if you have more information
  line_level_total_discount: number;
  selling_plan_allocation?: SellingPlanAllocation;
}

interface Discount {
  amount: number;
  title: string;
}

interface Image {
  alt: string;
  aspect_ratio: number;
  height: number;
  url: string;
  width: number;
}

interface OptionWithValue {
  name: string;
  value: string;
}

interface SellingPlanAllocation {
  price_adjustments: PriceAdjustment[];
  price: number;
  compare_at_price: number;
  per_delivery_price: number;
  selling_plan: SellingPlan;
}

interface PriceAdjustment {
  position: number;
  price: number;
}

interface SellingPlan {
  id: number;
  name: string;
  description: string | null;
  options: SellingPlanOption[];
  recurring_deliveries: boolean;
  price_adjustments: PriceAdjustmentDetail[];
  checkout_charge: CheckoutCharge;
}

interface SellingPlanOption {
  name: string;
  position: number;
  value: string;
}

interface PriceAdjustmentDetail {
  order_count: number | null;
  position: number;
  value_type: string;
  value: number;
}

interface CheckoutCharge {
  value_type: string;
  value: number;
}

interface DiscountApplication {
  amount: number;
  discount_application: DiscountApplicationDetail;
}

interface DiscountApplicationDetail {
  allocation_method: string;
  created_at: string;
  description: string | null;
  key: string;
  target_selection: string;
  target_type: string;
  title: string;
  total_allocated_amount: number;
  type: string;
  value: string;
  value_type: string;
}

interface ItemChange {
  product_id: number;
  variant_id: number;
  id: string;
  image: string;
  price: string;
  presentment_price: number;
  quantity: number;
  title: string;
  product_title: string;
  variant_title: string;
  vendor: string;
  product_type: string;
  sku: string;
  url: string;
  untranslated_product_title: string;
  untranslated_variant_title: string;
  view_key: string;
}
